import React, {useCallback, useRef, useState} from 'react';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Grid from 'brandComponents/Grid';

import {IPaginatedContentProps} from './IPaginatedContent';
import {PaginatedContentButton} from './Subcomponents';

import styles from './PaginatedContent.module.scss';

const LOADABLE_COUNT = 6;

const PaginatedContent = ({children, id, isOnlyColumns}: IPaginatedContentProps) => {
	const {managerRepository} = useGeneralProvider();
	const loadedItemsCountRef = useRef(LOADABLE_COUNT);
	const [loadedItems, setLoadedItems] = useState<React.ReactNode[]>([children]);
	const [hasMoreItems, setHasMoreItems] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	const handleLoadMore = useCallback(() => {
		setIsLoading(() => true);

		const cmsProvider = managerRepository.getContentMgr().getCMSProvider();
		cmsProvider
			.getContent('GetPaginatedContentItems', {
				variables: {
					ids: [id],
					contentSkip: loadedItemsCountRef.current,
				},
			})
			.then(({paginatedContentCollection}) => {
				const contentCollection = paginatedContentCollection?.items[0]?.contentCollection;
				const newItems = contentCollection?.items;
				const total = contentCollection?.total || loadedItemsCountRef.current;
				const newItemsBuilt = cmsProvider.getBuilder().build(
					newItems
				) as React.ReactNode;
				const newComponents = React.isValidElement(newItemsBuilt) ? Object.values(newItemsBuilt.props) : null;

				// Add keys to children
				const componentsWithKeys = newComponents.map((child: React.ReactNode, index) => {
					if (React.isValidElement(child)) {
						return React.cloneElement(child, {
							key: child.key || new Date().toString() + index,
						});
					}

					return child;
				});

				if (newItems.length) {
					loadedItemsCountRef.current += LOADABLE_COUNT;
					setLoadedItems((prevItems) => prevItems.concat(componentsWithKeys));
				}

				// Disable button if there are no more items
				if (!newItems.length || newItems.length < LOADABLE_COUNT || loadedItemsCountRef.current >= total) {
					setHasMoreItems(() => false);
				}

				setIsLoading(() => false);
			});
	}, []);

	if (isOnlyColumns) {
		// Loaded items are already columns
		return (
			<>
				{loadedItems}

				{hasMoreItems && (
					<Grid.Column>
						<PaginatedContentButton isLoading={isLoading} handleLoadMore={handleLoadMore} />
					</Grid.Column>
				)}
			</>
		);
	}

	return (
		<div className={styles['paginated-content']}>
			<div className={styles['paginated-content__items']}>{loadedItems}</div>

			{hasMoreItems && <PaginatedContentButton isLoading={isLoading} handleLoadMore={handleLoadMore} />}
		</div>
	);
};

export default PaginatedContent;
